import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import Form from "@/components/Form";
import { QueryTable } from "@/components/query-table/QueryTable";
import SaveButton from "@/components/SaveButton";
import Spinner from "@/components/Spinner";
import Title from "@/components/Title";
import { Module, PATH_PAYROLL, PATH_PAYROLL_SPLIT_DETAIL } from "@/constants";
import useQueryData from "@/hooks/useQueryData";
import { useSyncFormWithQueryData } from "@/hooks/useSyncFormWithQueryData";
import { usePayrollDetails } from "@/modules/payrolls/hooks/usePayrollDetails";
import { payrollSplit } from "@/schemas/payrollSplit";
import { FetchListRequest } from "@/services/api";
import { PayrollsService } from "@/services/payrolls";
import { PayrollSplit, UpdatePayrollSplit } from "@/types/payrollSplit";
import { zodResolver } from "@hookform/resolvers/zod";
import { memo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { generatePath, Navigate } from "react-router-dom";
import { usePayrollBreadcrumbs } from "../hooks/usePayrollBreadcrumbs";
import { usePayrollParams } from "../hooks/usePayrollParams";
import { usePayrollPermissions } from "../hooks/usePayrollPermissions";
import { usePayrollSplitColumnOverrides } from "../hooks/usePayrollSplitColumnOverrides";
import { useFormBaseline } from "@/hooks/useFormBaseline";
import { useFormDefaultValuesFromQuery } from "@/hooks/useFormDefaultValuesFromQuery";
import { useUpdatePayrollSplitMutation } from "../hooks/useUpdatePayrollSplitMutation";
import { writePayrollSplitFormSchema } from "../schema";
import { WritePayrollSplitForm } from "../types";
import { convertFormToUpdatePayrollDetails } from "../utils/convertFormToUpdatePayrollDetails";
import { PayrollCloseButton } from "./PayrollCloseButton";
import { PayrollCorrectionButton } from "./PayrollCorrectionButton";
import { PayrollSplitRecordView } from "./PayrollSplitRecordView";
import { initialColumnVisibility } from "../initialColumnVisibility";
import { PayrollSendContestConfirmationButton } from "./PayrollSendContestConfirmationButton";

const defaultValues: WritePayrollSplitForm = {
  entries: {},
};

export const PayrollSplitDirectory = memo(() => {
  const { t } = useTranslation();
  const { payrollId } = usePayrollParams();
  const permissions = usePayrollPermissions();

  const fetchListFn = useCallback(
    (params: FetchListRequest<PayrollSplit>) =>
      PayrollsService.getSplitList(payrollId, params),
    [payrollId],
  );

  const pathBuilder = useCallback(
    (row: PayrollSplit) =>
      generatePath(PATH_PAYROLL_SPLIT_DETAIL, {
        payrollId,
        entryId: row.id,
      }),
    [payrollId],
  );

  const { name, options, query, queryValue } = usePayrollDetails<PayrollSplit>({
    fetchListFn,
    nameSuffix: "split",
  });

  const breadcrumbs = usePayrollBreadcrumbs([
    {
      label: t("payrolls.views.split.title"),
    },
  ]);

  const columnOverrides = usePayrollSplitColumnOverrides();
  const mutation = useUpdatePayrollSplitMutation();

  const form = useForm<WritePayrollSplitForm>({
    resolver: zodResolver(writePayrollSplitFormSchema),
    defaultValues,
    disabled: !permissions.can_update && mutation.isPending,
  });

  const data = useQueryData(query);
  const formDefaultValues = useFormDefaultValuesFromQuery<
    PayrollSplit,
    "entries",
    WritePayrollSplitForm
  >(data);
  const { baseline, updateBaseline } = useFormBaseline(form, formDefaultValues);

  const handleSubmit = useCallback(
    async (values: WritePayrollSplitForm) => {
      const dirtyFields = form.formState.dirtyFields;
      const payload = convertFormToUpdatePayrollDetails<UpdatePayrollSplit>(
        values,
        dirtyFields,
      );
      await mutation.mutateAsync(payload);
      updateBaseline();
    },
    [form.formState.dirtyFields, mutation, updateBaseline],
  );

  useSyncFormWithQueryData(data, form);

  if (permissions.isLoading) {
    return <Spinner className="w-full h-full" />;
  }

  if (!permissions.canAccessSplit) {
    return (
      <Navigate
        to={generatePath(PATH_PAYROLL, {
          payrollId,
        })}
        replace
      />
    );
  }

  return (
    <QueryTable
      schema={payrollSplit}
      name={name}
      pathBuilder={pathBuilder}
      query={query}
      options={options}
      columnOverrides={columnOverrides}
      tableFeatures={{
        editionHighlight: { baseline },
        initialColumnVisibility,
        exportSettings: {
          moduleName: Module.Payrolls,
          queryParams: { payroll_stage: "split", payroll_id: payrollId },
        },
      }}
    >
      <QueryTable.Header
        breadcrumbLabel={`${name}.title.multiple`}
        breadcrumbs={breadcrumbs}
        actions={[
          permissions.canCloseSplit && <PayrollCloseButton />,
          permissions.canCorrect && <PayrollCorrectionButton />,
          <PayrollSendContestConfirmationButton />,
        ]}
      />
      <Title>{t("payrolls.views.split.title")}</Title>
      <PayrollSplitRecordView />
      <QueryTable.Menubar>
        <QueryTable.Menubar.Tools />
        <QueryTable.Menubar.Columns />
        <QueryTable.Menubar.Settings />
      </QueryTable.Menubar>
      <DataTableQueryContextProvider value={queryValue}>
        <Form form={form} handleSubmit={handleSubmit}>
          <QueryTable.Content>
            <QueryTable.Status>
              <QueryTable.DirtyRowsStatus />
            </QueryTable.Status>
          </QueryTable.Content>
          <footer className="flex py-2 justify-end gap-6">
            <SaveButton />
          </footer>
        </Form>
      </DataTableQueryContextProvider>
    </QueryTable>
  );
});
